import axios from 'axios';
import { BACKEND_URL } from './config';
import { clearSession, getToken } from './auth';

const client = axios.create({ baseURL: `${BACKEND_URL}/api` });

client.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// s159: trial / membership over → the brand home shows the pay modal
// (lmsbeta.aurexnetwork.com → beta.aurexnetwork.com, lms.carlosartiles.com → carlosartiles.com)
const brandHome = () => {
  const parts = window.location.hostname.split('.');
  const first = parts.shift();
  return `${window.location.protocol}//${/beta$/.test(first) ? 'beta.' : ''}${parts.join('.')}/?membership=expired`;
};
const membershipLapsed = (err) => err.response?.status === 403 && err.response?.data?.detail?.code === 'membership_lapsed';

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (membershipLapsed(err)) { window.location.assign(brandHome()); return Promise.reject(err); }
    if (err.response?.status === 401 && window.location.pathname !== '/login') {
      clearSession();
      window.location.assign('/login');
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (username, password) => client.post('/auth/login', { username, password }),
  ssoLogin: (token) => client.get('/auth/sso-login', { params: { token } }),
  me: () => client.get('/auth/me'),
  logout: () => client.post('/auth/logout'),
};

export const healthAPI = {
  check: () => client.get('/health'),
};

export const brandAPI = {
  get: () => client.get('/public/brand'),
};

export const publicAPI = {
  home: () => client.get('/public/home'),
  categories: () => client.get('/public/categories'),
  courses: (params = {}) => client.get('/public/courses', { params }),
  course: (slug) => client.get(`/public/courses/${slug}`),
  instructor: (memberId) => client.get(`/public/instructors/${memberId}`),
  page: (key) => client.get(`/public/pages/${key}`),
  frontend: () => client.get('/public/frontend'),
};

export const settingsAPI = {
  get: () => client.get('/admin/frontend-settings'),
  put: (data) => client.put('/admin/frontend-settings', data),
  uploadBanner: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/admin/frontend-settings/upload', fd);
  },
};

export const learnAPI = {
  myCourses: () => client.get('/me/courses'),
  view: (slug) => client.get(`/learn/${slug}`),
  progress: (courseId, data) => client.put(`/me/progress/${courseId}`, data),
  resetPreview: (courseId) => client.delete(`/me/progress/${courseId}`),
  quizSubmit: (lessonId, answers) => client.post(`/lessons/${lessonId}/quiz-submit`, { answers }),
  lessonHtml: (lessonId) => client.get(`/lessons/${lessonId}/html`),
  checkAnswer: (lessonId, questionId, picked) => client.post(`/lessons/${lessonId}/check-answer`, { question_id: questionId, picked }),
  checkSubmit: (lessonId, answers) => client.post(`/lessons/${lessonId}/check-submit`, { answers }),
  scorecardLaunch: (lessonId, ret) => client.post(`/lessons/${lessonId}/scorecard-launch`, { ret }),
  scorecardRefresh: (lessonId) => client.post(`/lessons/${lessonId}/scorecard-refresh`),
};

export const shopAPI = {
  enrollFree: (courseId) => client.post(`/courses/${courseId}/enroll-free`),
  register: (courseId) => client.post(`/courses/${courseId}/register`),
  cart: () => client.get('/me/cart'),
  cartAdd: (courseId) => client.post(`/me/cart/${courseId}`),
  cartRemove: (courseId) => client.delete(`/me/cart/${courseId}`),
  wishlist: () => client.get('/me/wishlist'),
  wishlistToggle: (courseId) => client.post(`/me/wishlist/${courseId}`),
  checkout: (courseIds, coupon = '', returnPath = '') => client.post('/checkout/stripe', { course_ids: courseIds, coupon, return_path: returnPath }),
  releaseCoupon: () => client.post('/coupons/release'),
  validateCoupon: (code, courseIds) => client.post('/coupons/validate', { code, course_ids: courseIds }),
  confirm: (sessionId) => client.post('/checkout/stripe/confirm', { session_id: sessionId }),
  purchases: () => client.get('/me/purchases'),
  invoice: (paymentId) => client.get(`/me/invoices/${paymentId}`),
  adminPayments: (params = {}) => client.get('/admin/payments', { params }),
  revenueReport: (params = {}) => client.get('/admin/revenue-report', { params }),
};

export const instructorAPI = {
  apply: (data) => client.post('/instructor/apply', data),
  applyUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/instructor/apply-upload', fd);
  },
  myApplication: () => client.get('/instructor/application'),
  sales: () => client.get('/instructor/sales'),
  balance: () => client.get('/instructor/balance'),
  withdrawals: () => client.get('/instructor/withdrawals'),
  requestWithdrawal: (data) => client.post('/instructor/withdrawals', data),
  adminApplications: (status = '') => client.get('/admin/instructor-applications', { params: { status } }),
  resolveApplication: (id, action, note = '') => client.put(`/admin/instructor-applications/${id}`, { action, note }),
  adminPayouts: (status = '') => client.get('/admin/payouts', { params: { status } }),
  resolvePayout: (id, action, note = '') => client.put(`/admin/payouts/${id}`, { action, note }),
};

export const mentorAPI = {
  calendar: () => client.get('/mentor/calendar'),
  bookings: () => client.get('/mentor/bookings'),
  book: (slotId, data) => client.post(`/mentor/book/${slotId}`, data || {}),
  cancel: (slotId) => client.post(`/mentor/cancel/${slotId}`),
  // Mentor side (s150): own LMS sessions + private notes (mentor-only)
  mySessions: () => client.get('/mentor/my-mentor-sessions'),
  saveNotes: (slotId, notes) => client.put(`/mentor/my-mentor-sessions/${slotId}/notes`, { notes }),
};

export const engageAPI = {
  rate: (courseId, data) => client.post(`/courses/${courseId}/rate`, data),
  myRating: (courseId) => client.get(`/courses/${courseId}/my-rating`),
  threads: () => client.get('/me/threads'),
  startThread: (toMemberId, body) => client.post('/me/threads', { to_member_id: toMemberId, body }),
  messages: (threadId) => client.get(`/threads/${threadId}/messages`),
  send: (threadId, body) => client.post(`/threads/${threadId}/messages`, { body }),
  notifications: () => client.get('/me/notifications'),
  markRead: () => client.post('/me/notifications/read'),
};

export const couponsAPI = {
  list: (params = {}) => client.get('/admin/coupons', { params }),
  create: (data) => client.post('/admin/coupons', data),
  remove: (id) => client.delete(`/admin/coupons/${id}`),
  release: (id) => client.post(`/admin/coupons/${id}/release`),
};

export const enrollAPI = {
  memberPool: (q) => client.get('/admin/member-pool', { params: { q } }),
  list: (params = {}) => client.get('/admin/enrollments', { params }),
  create: (data) => client.post('/admin/enrollments', data),
  remove: (id) => client.delete(`/admin/enrollments/${id}`),
  approve: (id) => client.post(`/admin/enrollments/${id}/approve`),
  reject: (id) => client.post(`/admin/enrollments/${id}/reject`),
  releaseNext: (id) => client.post(`/admin/enrollments/${id}/release-next`),
};

// s155 course Students tab (admin only)
export const studentsAPI = {
  list: (courseId, params = {}) => client.get(`/admin/courses/${courseId}/students`, { params }),
  get: (courseId, memberId) => client.get(`/admin/courses/${courseId}/students/${memberId}`),
};

export const catalogAPI = {
  list: () => client.get('/admin/categories'),
  create: (data) => client.post('/admin/categories', data),
  update: (id, data) => client.put(`/admin/categories/${id}`, data),
  remove: (id) => client.delete(`/admin/categories/${id}`),
  reorder: (ids) => client.put('/admin/categories/order', { ids }),
  upload: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/admin/categories/${id}/upload`, fd);
  },
};

export const coursesAPI = {
  list: (params = {}) => client.get('/admin/courses', { params }),
  create: (data) => client.post('/admin/courses', data),
  get: (id) => client.get(`/admin/courses/${id}`),
  update: (id, data) => client.put(`/admin/courses/${id}`, data),
  remove: (id) => client.delete(`/admin/courses/${id}`),
  upload: (id, kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/admin/courses/${id}/upload?kind=${kind}`, fd);
  },
  curriculum: (id) => client.get(`/admin/courses/${id}/curriculum`),
  createSection: (id, data) => client.post(`/admin/courses/${id}/sections`, data),
  reorderSections: (id, ids) => client.put(`/admin/courses/${id}/sections/order`, { ids }),
  updateSection: (sid, data) => client.put(`/admin/sections/${sid}`, data),
  deleteSection: (sid) => client.delete(`/admin/sections/${sid}`),
  createLesson: (sid, data) => client.post(`/admin/sections/${sid}/lessons`, data),
  reorderLessons: (sid, ids) => client.put(`/admin/sections/${sid}/lessons/order`, { ids }),
  updateLesson: (lid, data) => client.put(`/admin/lessons/${lid}`, data),
  deleteLesson: (lid) => client.delete(`/admin/lessons/${lid}`),
  clearLesson: (lid, what) => client.post(`/admin/lessons/${lid}/clear`, what),
  lessonHtml: (lid) => client.get(`/admin/lessons/${lid}/html`),
  checkResults: (lid) => client.get(`/admin/lessons/${lid}/check-results`),
  videoDuration: (url) => client.get('/admin/video-duration', { params: { url } }),
};

export default client;
